/**
 * Business expense report dataset helpers (primary category grouping under Business Expenses root).
 */

import type {
  BusinessExpensePrimaryCategoryGroup,
  ExpenseByCategoryRow,
  ReportTransactionRow,
} from '../../types/report.types';
import { findBusinessExpenseRootId, type CategoryWithTree } from './business-expense-scope';
import { expenseOutflowAmount, isExpenseOutflow } from './transaction-amount-utils';

export interface BusinessExpenseTransactionLike {
  id: string;
  date: string;
  amount: number;
  type: string | null;
  category_id: string | null;
  description: string | null;
  merchant_name: string | null;
  notes: string | null;
  account_id: string | null;
  archived_at?: string | null;
}

export function resolveBusinessPrimaryCategoryName(
  categoryId: string | null | undefined,
  categories: CategoryWithTree[],
  categoryMap: Map<string, { name: string }>,
  businessRootId: string | null
): string {
  if (!categoryId) return 'Uncategorised';
  const directName = categoryMap.get(categoryId)?.name;
  if (!businessRootId) return directName ?? 'Uncategorised';

  const byId = new Map(categories.map((c) => [c.id, c]));
  let node = byId.get(categoryId);
  if (!node) return directName ?? 'Uncategorised';

  if (node.id === businessRootId) {
    return categoryMap.get(businessRootId)?.name ?? 'Business Expenses';
  }

  while (node.parent_id && node.parent_id !== businessRootId) {
    const parent = byId.get(node.parent_id);
    if (!parent) break;
    node = parent;
  }

  if (node.parent_id === businessRootId) {
    return categoryMap.get(node.id)?.name ?? directName ?? 'Uncategorised';
  }

  return categoryMap.get(categoryId)?.name ?? 'Uncategorised';
}

export function buildBusinessExpensePrimaryCategoryGroups(
  transactions: BusinessExpenseTransactionLike[],
  categories: CategoryWithTree[],
  categoryMap: Map<string, { name: string }>,
  toRow: (tx: BusinessExpenseTransactionLike) => ReportTransactionRow
): BusinessExpensePrimaryCategoryGroup[] {
  const businessRootId = findBusinessExpenseRootId(categories);
  const groups = new Map<string, { amount: number; transactions: ReportTransactionRow[] }>();

  for (const tx of transactions) {
    if (!isExpenseOutflow(tx)) continue;
    const primaryName = resolveBusinessPrimaryCategoryName(
      tx.category_id,
      categories,
      categoryMap,
      businessRootId
    );
    const entry = groups.get(primaryName) ?? { amount: 0, transactions: [] };
    entry.amount += expenseOutflowAmount(tx);
    entry.transactions.push(toRow(tx));
    groups.set(primaryName, entry);
  }

  const total = Array.from(groups.values()).reduce((sum, g) => sum + g.amount, 0) || 1;

  return Array.from(groups.entries())
    .map(([primaryCategoryName, { amount, transactions: txs }]) => ({
      primaryCategoryName,
      amount,
      transactionCount: txs.length,
      percent: (amount / total) * 100,
      transactions: [...txs].sort((a, b) => a.date.localeCompare(b.date)),
    }))
    .sort((a, b) => b.amount - a.amount);
}

export function expenseRowsFromPrimaryGroups(
  groups: BusinessExpensePrimaryCategoryGroup[]
): ExpenseByCategoryRow[] {
  return groups.map((g) => ({
    categoryName: g.primaryCategoryName,
    amount: g.amount,
    transactionCount: g.transactionCount,
    percent: g.percent,
  }));
}
